import { NextPage } from "next";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import {
  ArticleWithPicture,
  ArticleWithPictureProps,
} from "@/components/common/articleWithPicture/ArticleWithPicture";
import { Section } from "@/components/common/section/Section";
import { Article, ArticleProps } from "@/components/common/article/Article";

import { StaticMap } from "@/components/common/staticMap/StaticMap";
import { DepartmentDetails } from "@/interfaces/departmentDetails";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1, {
  RentACarReview,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import {
  CityPropositionsBlock,
  CityPropositionsBlockProps,
} from "@/components/pages/agencies/cityPropositionsBlock/CityPropositionsBlock";
import styles from "./Agencies.module.scss";
import {
  DepartmentBlock,
  DepartmentBlockContent,
} from "./departmentsBlock/DepartmentBlock";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type AgenciesContent = {
  heroBlock: HeroBlockProps;
  departmentBlock: DepartmentBlockContent;
  leftArticleWithPicture: ArticleWithPictureProps | undefined;
  middleArticle: ArticleProps | undefined;
  rightArticleWithPicture: ArticleWithPictureProps | undefined;
  rentACarReview: RentACarReview | undefined;
  seo: SEO;
  seoWithoutTemplate: SEOWithoutTemplate;
  cityPropositionsBlock: CityPropositionsBlockProps | undefined;
};
type AgenciesProps = PageProps &
  AgenciesContent & {
    departments: DepartmentDetails[];
    defaultCanonicalRoute: string;
  };

export const Agencies: NextPage<AgenciesProps> = ({
  layout,
  heroBlock,
  reviewsConst,
  departmentBlock,
  departments,
  leftArticleWithPicture,
  middleArticle,
  rightArticleWithPicture,
  rentACarReview,
  seo,
  seoWithoutTemplate,
  defaultCanonicalRoute,
  cityPropositionsBlock,
}: AgenciesProps) => {
  const intl = useIntl();

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: `agenciesBreadcrumb` }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <HeroBlock
          {...heroBlock}
          searchBlockInitialState={{
            vehicleType: VehicleType.CAR,
            isOneWayTrip: false,
          }}
        />
        <Section containerStyle={styles.map}>
          <StaticMap
            mapName={"toutes-les-agences-rent-a-car-v2_1118492"}
            forceX={46.85}
            forceY={2.34}
            forceZoom={"6"}
          />
        </Section>
        {cityPropositionsBlock && (
          <Section>
            <CityPropositionsBlock {...cityPropositionsBlock} />
          </Section>
        )}
        {departments.length > 0 && (
          <DepartmentBlock
            {...departmentBlock}
            departments={departments}
          ></DepartmentBlock>
        )}
        {leftArticleWithPicture && (
          <Section containerStyle={styles.leftArticle}>
            <ArticleWithPicture {...leftArticleWithPicture} />
          </Section>
        )}
        {middleArticle && (
          <Section containerStyle={styles.darkBg}>
            <Article {...middleArticle} containerStyle={styles.article} />
          </Section>
        )}
        {rightArticleWithPicture && (
          <Section>
            <ArticleWithPicture {...rightArticleWithPicture} />
          </Section>
        )}
        {rentACarReview && rentACarReview.clientReviews.length > 0 && (
          <ReviewsBlock
            rentACarReview={rentACarReview}
            reviewsConst={reviewsConst}
          />
        )}
      </Layout>
    </>
  );
};
